/**
 * What the planting feeds and shelters, and when.
 *
 * ## One calendar, derived twice over
 *
 * The catalog says *Ilex verticillata* offers fruit for about ninety days after it ripens. The site
 * profile says when it ripens here. Put the two together and you get "bird food, September to
 * December" — for this garden, from one canonical record, with nothing stored per ZIP code. That is
 * the whole mechanism, and it is the reason ecology lives downstream of {@link ResolvedPhenology}
 * rather than beside it: move the site and every resource window moves with the bloom it hangs off,
 * automatically, because it was never a date in the first place.
 *
 * ## What a site-level calendar is for
 *
 * Summed across the planting, the windows answer questions no single plant can: is there nectar in
 * every week bees are flying, is there fruit through the winter, does anything host caterpillars in
 * June. Those are **continuity** questions, and continuity is where real plantings fail — a garden
 * with eleven summer perennials and nothing in March is a garden with a March famine, however good
 * the list looks.
 *
 * So {@link buildWildlifeCalendar} produces monthly bands and then goes looking for the holes in
 * them. The bands are intentionally coarse: twelve buckets is the resolution at which a planting
 * decision is actually made, and a daily curve would imply the underlying phenology is accurate to
 * the day, which it is not.
 */

import type { FloraDefinition } from '@/flora/FloraDefinition';
import {
  FOOD_RESOURCE_KINDS,
  HABITAT_RESOURCE_KINDS,
  ecologyScore,
  foodResourceLabel,
  habitatResourceLabel,
  wildlifeGroupsOf,
  type EcologyPhase,
  type FoodResourceKind,
  type HabitatResourceKind,
  type LarvalHostRelation,
  type WildlifeGroup,
} from '@/flora/FloraEcology';
import { DAYS_IN_YEAR, dayOfYearFromDate } from '@/environment/SeasonModel';
import { windowContains, type DayWindow, type ResolvedPhenology } from './Phenology';
import type { SiteLocalization } from './SiteLocalization';

/* ------------------------------ Per-plant resolution ----------------------------- */

export interface ResolvedFoodWindow {
  kind: FoodResourceKind;
  window: DayWindow;
  /** The record's value, unchanged — localization moves *when*, never *how much*. */
  value: number;
  consumers: WildlifeGroup[];
  note?: string;
}

export interface ResolvedHabitatWindow {
  kind: HabitatResourceKind;
  window: DayWindow;
  value: number;
  users: WildlifeGroup[];
  note?: string;
}

export interface ResolvedEcology {
  food: ResolvedFoodWindow[];
  habitat: ResolvedHabitatWindow[];
  larvalHosts: LarvalHostRelation[];
  /** Every group this plant does anything for. */
  groups: WildlifeGroup[];
  /** 0–1, for ranking. See `ecologyScore`. */
  score: number;
  keystone: boolean;
  /** False when the record carries no ecology block at all — reported, not filled in. */
  described: boolean;
}

/**
 * The day windows a phase resolves to.
 *
 * Returns a list rather than one window because bloom legitimately happens more than once: a
 * reblooming shrub offers two separate nectar periods, and flattening them into one span from the
 * first flower to the last would claim food through a midsummer gap that is really there.
 */
function windowsForPhase(phase: EcologyPhase, phenology: ResolvedPhenology): DayWindow[] {
  switch (phase) {
    case 'bloom':
      return phenology.bloomWindows.map((window) => ({
        startDay: window.startDayOfYear,
        endDay: window.endDayOfYear,
      }));
    case 'fruit':
      return phenology.fruitWindow ? [phenology.fruitWindow] : [];
    case 'seed':
      // Seed falls back to fruit: for a great many plants they are the same event described from two
      // sides, and a record that gave fruit timings but no seed timings still knows when its seed is.
      return phenology.seedWindow
        ? [phenology.seedWindow]
        : phenology.fruitWindow
          ? [phenology.fruitWindow]
          : [];
    case 'foliage':
      return [phenology.foliageWindow];
    case 'year-round':
      return [{ startDay: 1, endDay: DAYS_IN_YEAR }];
  }
}

/** Resolve one plant's ecological offering against its own localized calendar. */
export function resolveEcology(
  definition: FloraDefinition,
  phenology: ResolvedPhenology,
): ResolvedEcology {
  const ecology = definition.ecology;
  const food: ResolvedFoodWindow[] = [];
  const habitat: ResolvedHabitatWindow[] = [];

  for (const resource of ecology?.food ?? []) {
    for (const window of windowsForPhase(resource.phase, phenology)) {
      food.push({
        kind: resource.kind,
        window,
        value: resource.value,
        consumers: [...resource.consumers],
        ...(resource.note ? { note: resource.note } : {}),
      });
    }
  }

  for (const resource of ecology?.habitat ?? []) {
    for (const window of windowsForPhase(resource.phase, phenology)) {
      habitat.push({
        kind: resource.kind,
        window,
        value: resource.value,
        users: [...resource.users],
        ...(resource.note ? { note: resource.note } : {}),
      });
    }
  }

  return {
    food,
    habitat,
    larvalHosts: (ecology?.larvalHosts ?? []).map((host) => ({ ...host })),
    groups: wildlifeGroupsOf(ecology),
    score: ecologyScore(ecology),
    keystone: ecology?.keystone === true,
    described: ecology !== undefined,
  };
}

/* -------------------------------- Site calendar --------------------------------- */

export const MONTH_LABELS: readonly string[] = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
];

/** First and last day of year of each month, computed once from the calendar the app runs on. */
const MONTH_BOUNDS: readonly { start: number; end: number }[] = MONTH_LABELS.map((_, index) => {
  const month = index + 1;
  const start = dayOfYearFromDate(month, 1);
  const end = month === 12 ? DAYS_IN_YEAR : dayOfYearFromDate(month + 1, 1) - 1;
  return { start, end };
});

/** How much of a month a window covers, in [0, 1]. */
function monthCoverage(window: DayWindow, monthIndex: number): number {
  const bounds = MONTH_BOUNDS[monthIndex];
  if (!bounds) return 0;
  let covered = 0;
  for (let day = bounds.start; day <= bounds.end; day += 1) {
    if (windowContains(window, day)) covered += 1;
  }
  return covered / (bounds.end - bounds.start + 1);
}

/** One resource across the year. `months[0]` is January. */
export interface CalendarRow<Kind extends string = string> {
  kind: Kind;
  label: string;
  /** 0–1 per month, saturating — see {@link saturate}. */
  months: number[];
  /** Distinct plants contributing to this row. */
  contributors: number;
}

/** A plant on the site, with how many of it there are. */
export interface CalendarEntry {
  definition: FloraDefinition;
  ecology: ResolvedEcology;
  /** Number of specimens planted. Ten winterberries feed more birds than one does. */
  count: number;
}

/**
 * Turn a sum of resource-days into a 0–1 band.
 *
 * Saturating rather than linear, and the shape matters more than the constant: the interesting
 * distinction is between *nothing*, *something*, and *plenty*, and a linear scale normalized to the
 * site's own maximum would redraw every band whenever one more plant was added — making a garden look
 * worse for gaining a shrub. This scale is absolute, so bands only ever grow when the planting does.
 */
function saturate(total: number): number {
  return 1 - Math.exp(-total / 1.6);
}

export interface WildlifeCalendar {
  food: CalendarRow<FoodResourceKind>[];
  habitat: CalendarRow<HabitatResourceKind>[];
  gaps: EcologyGap[];
  /** Plants on the site whose record carries an ecology block. */
  describedSpecies: number;
  /** Species on the site in total, so the report can say what it does not know. */
  totalSpecies: number;
  /** Larval-host relationships present anywhere in the planting. */
  larvalHostSpecies: number;
}

/**
 * A hole in the year.
 *
 * Reported as month ranges rather than as a score, because "nothing flowers between the last aster
 * and the first willow" is an instruction and "pollinator continuity: 0.62" is not.
 */
export interface EcologyGap {
  id: 'nectar' | 'fruit-and-seed' | 'winter-cover' | 'host-foliage';
  label: string;
  /** Month indices, 0-based, where the resource is effectively absent. */
  months: number[];
  message: string;
}

/** Below this a band is treated as empty for gap detection — a trace is not a food supply. */
const GAP_THRESHOLD = 0.12;

function buildRow<Kind extends string>(
  kind: Kind,
  label: string,
  windows: readonly { window: DayWindow; value: number; owner: number; count: number }[],
): CalendarRow<Kind> | undefined {
  if (windows.length === 0) return undefined;
  const months = MONTH_LABELS.map((_, index) => {
    let total = 0;
    for (const entry of windows) {
      // Count is dampened rather than linear: the tenth serviceberry adds real food, but not as much
      // as the first, and a hedge of two hundred should not saturate every band on the chart.
      total += entry.value * monthCoverage(entry.window, index) * Math.sqrt(entry.count);
    }
    return saturate(total);
  });
  return { kind, label, months, contributors: new Set(windows.map((w) => w.owner)).size };
}

/**
 * Build the site's food and habitat calendar from its planting.
 *
 * `entries` is one row per *species*, carrying how many are planted — the caller groups the site's
 * instances, because it is the one that knows what counts as a plant (a hedge of forty is forty).
 */
export function buildWildlifeCalendar(
  entries: readonly CalendarEntry[],
  localization: SiteLocalization,
): WildlifeCalendar {
  const foodWindows = new Map<
    FoodResourceKind,
    { window: DayWindow; value: number; owner: number; count: number }[]
  >();
  const habitatWindows = new Map<
    HabitatResourceKind,
    { window: DayWindow; value: number; owner: number; count: number }[]
  >();

  let describedSpecies = 0;
  let larvalHostSpecies = 0;

  entries.forEach((entry, owner) => {
    if (entry.ecology.described) describedSpecies += 1;
    if (entry.ecology.larvalHosts.length > 0) larvalHostSpecies += 1;
    for (const resource of entry.ecology.food) {
      const list = foodWindows.get(resource.kind) ?? [];
      list.push({ window: resource.window, value: resource.value, owner, count: entry.count });
      foodWindows.set(resource.kind, list);
    }
    for (const resource of entry.ecology.habitat) {
      const list = habitatWindows.get(resource.kind) ?? [];
      list.push({ window: resource.window, value: resource.value, owner, count: entry.count });
      habitatWindows.set(resource.kind, list);
    }
  });

  const food = FOOD_RESOURCE_KINDS.map((kind) =>
    buildRow(kind, foodResourceLabel(kind), foodWindows.get(kind) ?? []),
  ).filter((row): row is CalendarRow<FoodResourceKind> => row !== undefined);

  const habitat = HABITAT_RESOURCE_KINDS.map((kind) =>
    buildRow(kind, habitatResourceLabel(kind), habitatWindows.get(kind) ?? []),
  ).filter((row): row is CalendarRow<HabitatResourceKind> => row !== undefined);

  return {
    food,
    habitat,
    gaps: findGaps(food, habitat, localization),
    describedSpecies,
    totalSpecies: entries.length,
    larvalHostSpecies,
  };
}

/** Combine several rows into one band by taking the strongest contributor in each month. */
function combine(rows: readonly CalendarRow<string>[]): number[] {
  return MONTH_LABELS.map((_, index) =>
    rows.reduce((best, row) => Math.max(best, row.months[index] ?? 0), 0),
  );
}

/** Month indices where a band is below the threshold, restricted to the months that matter. */
function emptyMonths(band: readonly number[], relevant: readonly number[]): number[] {
  return relevant.filter((index) => (band[index] ?? 0) < GAP_THRESHOLD);
}

/** `March`, `March–May`, `March, July and October` — month lists read as prose. */
export function describeMonths(months: readonly number[]): string {
  const names = months.map((index) => MONTH_LABELS[index] ?? '');
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] as string;

  // Contiguous runs read as ranges; scattered months read as a list.
  const contiguous = months.every((month, index) => index === 0 || month === (months[index - 1] ?? -9) + 1);
  if (contiguous) return `${names[0]}–${names[names.length - 1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The months a gap is worth reporting in.
 *
 * A nectar gap in January is not a finding in Vermont — nothing is flying — and reporting one would
 * bury the March gap that matters under eleven months of noise. The growing season comes from the
 * site's own frost dates, which is exactly the localization the rest of this file runs on.
 */
function growingMonths(localization: SiteLocalization): number[] {
  const months: number[] = [];
  for (let index = 0; index < MONTH_LABELS.length; index += 1) {
    const bounds = MONTH_BOUNDS[index];
    if (!bounds) continue;
    const inSeason =
      bounds.end >= localization.frost.lastSpringFrostDay - 20 &&
      bounds.start <= localization.frost.firstAutumnFrostDay + 20;
    if (inSeason) months.push(index);
  }
  return months;
}

function findGaps(
  food: readonly CalendarRow<FoodResourceKind>[],
  habitat: readonly CalendarRow<HabitatResourceKind>[],
  localization: SiteLocalization,
): EcologyGap[] {
  const gaps: EcologyGap[] = [];
  const allMonths = MONTH_LABELS.map((_, index) => index);
  const season = growingMonths(localization);

  const nectar = combine(food.filter((row) => row.kind === 'nectar' || row.kind === 'pollen'));
  const nectarGap = emptyMonths(nectar, season);
  if (nectarGap.length > 0) {
    gaps.push({
      id: 'nectar',
      label: 'Nectar and pollen',
      months: nectarGap,
      message:
        `Nothing on this site offers nectar or pollen in ${describeMonths(nectarGap)}, which falls ` +
        'inside the season pollinators are active here.',
    });
  }

  const birdFood = combine(
    food.filter((row) => ['berries', 'fleshy-fruit', 'seeds', 'nuts'].includes(row.kind)),
  );
  const birdGap = emptyMonths(birdFood, allMonths);
  if (birdGap.length > 0) {
    gaps.push({
      id: 'fruit-and-seed',
      label: 'Fruit and seed',
      months: birdGap,
      message:
        `No fruit, seed or mast is available in ${describeMonths(birdGap)}. Birds hold territory ` +
        'year round, and the lean months are the ones that decide whether they stay.',
    });
  }

  // Winter cover is a different question from winter food and fails independently: a garden can be
  // full of berries and offer nothing to sit in out of the wind.
  const winter = combine(
    habitat.filter((row) => ['winter-cover', 'cover', 'shelter', 'standing-stems'].includes(row.kind)),
  );
  const winterMonths = [10, 11, 0, 1, 2];
  const winterGap = emptyMonths(winter, winterMonths);
  if (winterGap.length > 0) {
    gaps.push({
      id: 'winter-cover',
      label: 'Winter cover',
      months: winterGap,
      message:
        `Little cover or standing structure through ${describeMonths(winterGap)}. Leaving stems ` +
        'and seed heads up until spring is the cheapest fix there is.',
    });
  }

  const host = combine(food.filter((row) => row.kind === 'host-foliage'));
  const hostGap = emptyMonths(host, season);
  if (hostGap.length === season.length && season.length > 0) {
    gaps.push({
      id: 'host-foliage',
      label: 'Host foliage',
      months: season,
      message:
        'No plant here is recorded as a larval host. Nectar feeds adult butterflies; only host ' +
        'foliage produces the next generation.',
    });
  }

  return gaps;
}

/** `█ ░ ·` bands, the way the spec sketches the calendar for a text or CSV export. */
export function calendarGlyphs(row: CalendarRow<string>): string {
  return row.months.map((value) => (value >= 0.55 ? '█' : value >= GAP_THRESHOLD ? '░' : '·')).join(' ');
}
